import { ReactNode } from "react";

import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";

export type SubscriptionActionButtonProps = {
  label: string;
  /** When set, the button is greyed out and explains itself on hover/tap. */
  disabledHint?: ReactNode;
  /** Disabled without a tooltip, e.g. when required backend data is missing. */
  disabled?: boolean;
  variant?: "primary" | "secondary";
  dataTestid?: string;
} & (
  | { target: string; onClick?: never }
  | { target?: never; onClick: () => void }
);

/**
 * The small primary button in a section header ("Ändern", "Abo pausieren"),
 * which turns into a disabled button with a tooltip whenever the action is
 * currently unavailable. Navigates via `target` (mobile: a genuine route,
 * e.g. the auto-renewal confirmation screen) or runs `onClick` in place
 * (desktop: opens the same confirmation inline, without changing the page).
 */
export const SubscriptionActionButton = ({
  label,
  target,
  onClick,
  disabledHint,
  disabled = false,
  variant = "primary",
  dataTestid,
}: SubscriptionActionButtonProps) => {
  const isDisabled = disabled || !!disabledHint;

  const button = target ? (
    <ButtonLink
      data-testid={dataTestid}
      size="sm"
      variant={variant}
      disabled={isDisabled}
      to={target}
      replace
    >
      {label}
    </ButtonLink>
  ) : (
    <Button
      dataTestid={dataTestid}
      size="sm"
      variant={variant}
      disabled={isDisabled}
      onClick={onClick}
    >
      {label}
    </Button>
  );

  if (!disabledHint) {
    return button;
  }

  return (
    <HintTooltip
      dataTestid={`${dataTestid}-hint`}
      content={disabledHint}
      // Prefer the right edge: these CTAs sit on the right of the card.
      // HintTooltip still clamps to the viewport so iPad/phone overflow-x-clip
      // cannot cut the bubble.
      align="right"
    >
      {button}
    </HintTooltip>
  );
};
